import { useTranslation } from "@impero-com/common/utils/i18n";
import { queryClient } from "@impero-com/common/utils/query_client";
import { Button, emitNotification, Modal } from "@impero-com/ui";

import { CommentField } from "components/modals/CommentField";
import { Content } from "components/modals/Content";
import { useModalComment } from "components/modals/useModalComment";
import { getPendingTestingActivitiesQueryKey } from "org/dashboardPendingActivities/queries/usePendingTestingActivitiesQuery";

import { usePostTestingActivitiesArchive } from "../../../../api/generated/testing-activities/testing-activities";
import { getGetTestingProgramsTestingProgramIdActivitiesQueryKey } from "../../../../api/generated/testing-programs/testing-programs";
import { getTestingActivityQueryKey } from "../view/queries/useTestingActivityQuery";

interface ArchiveTestingActivitiesModalProps {
    open: boolean;
    close: () => void;
    deselectAll: () => void;
    testingActivityIds: string[];
    testingProgramId: number;
}

export function ArchiveTestingActivitiesModal({
    open,
    close,
    deselectAll,
    testingActivityIds,
    testingProgramId,
}: ArchiveTestingActivitiesModalProps) {
    const { t } = useTranslation();
    const { comment, setComment, trimmed, isValid, isTooLong } =
        useModalComment({
            open,
        });
    const { mutate, isPending } = usePostTestingActivitiesArchive({
        mutation: {
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey:
                        getGetTestingProgramsTestingProgramIdActivitiesQueryKey(
                            testingProgramId
                        ),
                });
                queryClient.invalidateQueries({
                    queryKey: getPendingTestingActivitiesQueryKey(),
                });
                testingActivityIds.forEach((id) => {
                    queryClient.invalidateQueries({
                        queryKey: getTestingActivityQueryKey(id),
                    });
                });
                emitNotification(
                    "success",
                    t("TestingActivities_ArchiveModal_Success", {
                        count: testingActivityIds.length,
                    })
                );
                deselectAll();
                close();
            },
        },
    });

    const handleSubmitClick = () => {
        mutate({
            data: {
                testingActivityIds,
                comment: trimmed || undefined,
            },
        });
    };

    const okButton = {
        label: t("Archive"),
        loading: isPending,
        disabled: testingActivityIds.length === 0 || !isValid,
        onClick: handleSubmitClick,
    };

    const cancelButton = {
        label: t("Cancel"),
        onClick: close,
    };

    return (
        <Modal
            variant="content"
            open={open}
            onClose={cancelButton.onClick}
            title={t("TestingActivities_ArchiveModal_Title")}
            footer={
                <>
                    <Button onClick={cancelButton.onClick}>
                        {cancelButton.label}
                    </Button>
                    <Button
                        variant="primary"
                        loading={okButton.loading}
                        disabled={okButton.disabled}
                        onClick={okButton.onClick}
                    >
                        {okButton.label}
                    </Button>
                </>
            }
        >
            <Content body={t("TestingActivities_ArchiveModal_Body")}>
                <CommentField
                    value={comment}
                    onChange={setComment}
                    label={t("TestingActivities_ArchiveModal_CommentLabel")}
                    placeholder={t(
                        "TestingActivities_ArchiveModal_CommentPlaceholder"
                    )}
                    error={
                        isTooLong
                            ? t("TestingActivities_ArchiveModal_CommentError")
                            : undefined
                    }
                />
            </Content>
        </Modal>
    );
}
